import { Component, EventEmitter, Output } from '@angular/core';
import { PayrollService } from 'src/app/services/payroll.service';
import { UtilsService } from 'src/app/services/utils.service';
import { PermissionService } from 'src/app/services/permission.service';

declare const $: any;

interface ReadyReceipt {
    id: number;
    employee_name: string;
    period_label: string;
    net_paid: number;
    selected: boolean;
}

interface BatchItem {
    id: number;
    payroll_id: number;
    employee_name: string;
    period_label: string;
    status: 'pending' | 'processing' | 'stamped' | 'error' | 'skipped';
    message: string | null;
    duration_ms: number | null;
}

interface BatchState {
    id: number;
    status: 'processing' | 'completed';
    total: number;
    processed: number;
    percent: number;
    stamped_count: number;
    error_count: number;
    skipped_count: number;
    items: BatchItem[];
}

interface ValidationCategory {
    key: string;
    label: string;
    severity: 'error' | 'warning';
    issue_count: number;
}

interface ValidationWorkerError {
    employee_id: number | null;
    employee_name: string;
    message: string;
}

interface ValidationResult {
    total: number;
    categories: ValidationCategory[];
    workers_with_errors: ValidationWorkerError[];
    errored_payroll_ids: number[];
}

/**
 * Asistente de timbrado masivo de 4 pasos (Figma: RR.HH, node 1863:11078):
 * seleccionar recibos, validar información fiscal del colaborador,
 * confirmar, y ver el progreso en vivo del timbrado.
 *
 * El paso 4 no tiene un worker de colas real detras (QUEUE_CONNECTION=sync):
 * el "progreso en vivo" se logra llamando repetidamente a
 * processStampBatchChunk, que timbra unos cuantos recibos por llamada y
 * devuelve el estado acumulado real (persistido en DB), no simulado.
 */
@Component({
    selector: 'app-stamp-wizard-modal',
    templateUrl: './stamp-wizard-modal.component.html',
    styleUrls: ['./stamp-wizard-modal.component.css']
})
export class StampWizardModalComponent {
    @Output() completed = new EventEmitter<void>();

    modalId = 'stampWizardModal';
    step: 1 | 2 | 3 | 4 = 1;

    loadingList = false;
    receipts: ReadyReceipt[] = [];

    loadingValidation = false;
    validation: ValidationResult | null = null;
    excludeErrored = false;

    batch: BatchState | null = null;
    polling = false;
    showAllItems = false;

    private filters: any = {};

    constructor(
        private payrollService: PayrollService,
        private utils: UtilsService,
        private permissionService: PermissionService
    ) { }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Recibos electrónicos', action);
    }

    open(filters: any = {}): void {
        this.filters = filters || {};
        this.step = 1;
        this.validation = null;
        this.excludeErrored = false;
        this.batch = null;
        this.polling = false;
        this.showAllItems = false;
        this.loadReadyReceipts();
        $(`#${this.modalId}`).modal('show');
    }

    close(): void {
        $(`#${this.modalId}`).modal('hide');
    }

    cancel(): void {
        // Durante el timbrado en vivo no se permite cerrar (igual que el
        // diseño: "por favor no cierres esta ventana").
        if (this.step === 4 && this.batch?.status !== 'completed') {
            return;
        }
        this.close();
    }

    private loadReadyReceipts(): void {
        this.loadingList = true;
        this.payrollService.readyToStamp(this.filters).subscribe({
            next: (resp: any) => {
                this.loadingList = false;
                this.receipts = (resp?.data || []).map((r: any) => ({ ...r, selected: true }));
            },
            error: () => {
                this.loadingList = false;
                this.receipts = [];
            }
        });
    }

    get allSelected(): boolean {
        return this.receipts.length > 0 && this.receipts.every(r => r.selected);
    }

    toggleAll(checked: boolean): void {
        this.receipts.forEach(r => r.selected = checked);
    }

    get selectedReceipts(): ReadyReceipt[] {
        return this.receipts.filter(r => r.selected);
    }

    get selectedCount(): number {
        return this.selectedReceipts.length;
    }

    get selectedTotal(): number {
        return this.selectedReceipts.reduce((sum, r) => sum + (Number(r.net_paid) || 0), 0);
    }

    get selectedPeriodLabel(): string {
        const labels = Array.from(new Set(this.selectedReceipts.map(r => r.period_label).filter(Boolean)));
        if (labels.length === 0) return '--';
        if (labels.length === 1) return labels[0];
        return 'Varios periodos';
    }

    get estimatedTimeLabel(): string {
        // ~2s promedio por recibo observado contra el sandbox real de SW.
        const seconds = Math.max(1, this.selectedCount) * 2;
        if (seconds < 60) return `${seconds} segundos`;
        const minutes = Math.ceil(seconds / 60);
        return minutes === 1 ? '1 minuto' : `${minutes} minutos`;
    }

    formatCurrency(value: any): string {
        const num = Number(value) || 0;
        return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    }

    /** Paso 1 -> 2: valida la información fiscal de los colaboradores elegidos. */
    goToValidation(): void {
        if (this.selectedCount === 0) return;
        this.step = 2;
        this.loadingValidation = true;
        this.validation = null;
        this.excludeErrored = false;
        const ids = this.selectedReceipts.map(r => r.id);
        this.payrollService.validateSelection(ids).subscribe({
            next: (resp: any) => {
                this.loadingValidation = false;
                this.validation = resp as ValidationResult;
            },
            error: () => {
                this.loadingValidation = false;
                this.validation = null;
            }
        });
    }

    backToSelection(): void {
        this.step = 1;
    }

    get workersWithErrorsCount(): number {
        return this.validation?.workers_with_errors?.length || 0;
    }

    categoryIcon(category: ValidationCategory): 'check' | 'alert' {
        return category.issue_count > 0 ? 'alert' : 'check';
    }

    categoryDisplayText(category: ValidationCategory): string {
        if (category.issue_count === 0) {
            return String(this.validation?.total || 0);
        }
        return category.severity === 'warning'
            ? `${category.issue_count} pendientes`
            : `${category.issue_count} error`;
    }

    /** Paso 2 -> 3: si se pide excluir errores, se quitan del set seleccionado antes de confirmar. */
    goToConfirmation(): void {
        if (this.excludeErrored && this.validation?.errored_payroll_ids?.length) {
            const errored = new Set(this.validation.errored_payroll_ids);
            this.receipts.forEach(r => {
                if (errored.has(r.id)) {
                    r.selected = false;
                }
            });
        }
        if (this.selectedCount === 0) return;
        this.step = 3;
    }

    backToValidation(): void {
        this.step = 2;
    }

    /** Paso 3 -> 4: crea el lote y arranca el polling de avance. */
    startStamping(): void {
        const ids = this.selectedReceipts.map(r => r.id);
        this.step = 4;
        this.polling = true;
        this.payrollService.createStampBatch(ids).subscribe({
            next: (resp: any) => {
                this.batch = this.normalizeBatch(resp);
                this.advanceBatch();
            },
            error: (err: any) => {
                this.polling = false;
                const text = (typeof err === 'string' && err) || 'No se pudo iniciar el timbrado masivo';
                this.utils.alertSwal({ title: 'Error', text, icon: 'error' });
                this.step = 3;
            }
        });
    }

    private normalizeBatch(resp: any): BatchState {
        return {
            id: resp.id,
            status: resp.status,
            total: resp.total,
            processed: resp.processed,
            percent: resp.percent,
            stamped_count: resp.stamped_count,
            error_count: resp.error_count,
            skipped_count: resp.skipped_count,
            items: resp.items || []
        };
    }

    private advanceBatch(): void {
        if (!this.batch || this.batch.status === 'completed') {
            this.polling = false;
            if (this.batch?.status === 'completed') {
                this.completed.emit();
                // "Esta ventana se cerrará automáticamente al finalizar el proceso."
                setTimeout(() => this.close(), 1200);
            }
            return;
        }

        this.payrollService.processStampBatchChunk(this.batch.id).subscribe({
            next: (resp: any) => {
                this.batch = this.normalizeBatch(resp);
                this.advanceBatch();
            },
            error: () => {
                // Un fallo de red a mitad de lote no debe perder el progreso
                // ya guardado en DB: se reintenta el siguiente fragmento.
                setTimeout(() => this.advanceBatch(), 1500);
            }
        });
    }

    get processingCount(): number {
        if (!this.batch) return 0;
        return Math.max(0, this.batch.total - this.batch.processed);
    }

    get visibleItems(): BatchItem[] {
        if (!this.batch) return [];
        return this.showAllItems ? this.batch.items : this.batch.items.slice(0, 6);
    }

    get hasMoreItems(): boolean {
        return (this.batch?.items?.length || 0) > 6 && !this.showAllItems;
    }

    statusLabel(status: BatchItem['status']): string {
        const labels: { [k: string]: string } = {
            pending: 'Pendiente',
            processing: 'En proceso',
            stamped: 'Timbrado',
            error: 'Error',
            skipped: 'Omitido'
        };
        return labels[status] || status;
    }
}
